import Eyebrow from "../components/shared/Eyebrow";
import RevealText from "../components/shared/RevealText";
import HeroFeatures from "../components/shared/HeroFeatures";
import HeroQuoteForm from "../components/shared/HeroQuoteForm";
import WhatsAppButton from "../components/shared/WhatsAppButton";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

/**
 * The hero used on every page.
 *
 * Layout follows the client's reference: headline and reassurance points on
 * the left, a short quote form on the right, over a full-bleed photograph.
 * The form is the point — the brief asks for the quote action above the fold
 * on every page rather than only on Contact, because each service page is a
 * paid landing page and sending that traffic hunting for a form loses it.
 *
 * The DOM order is headline → form → reassurance + buttons, which is the
 * order a phone shows them. Desktop puts the form in its own column beside
 * the text using explicit grid placement rather than reordering, so the two
 * layouts read the same way without duplicating markup.
 *
 * `defaultService` pre-selects the service when the hero sits on a service
 * page, so the enquiry arrives already saying what it is about.
 */
export default function PageHero({
  eyebrow,
  headline,
  intro,
  image,
  imageAlt,
  showQuoteForm = true,
  defaultService = "",
  compact = false,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <section
      ref={ref}
      className={`relative bg-ink text-white overflow-hidden pt-20 lg:pt-24 ${
        compact ? "" : "lg:min-h-[80svh]"
      }`}
      data-testid="page-hero"
    >
      {/* Full-bleed photograph behind everything. */}
      <div className="absolute inset-0 z-0">
        <motion.div style={{ y }} className="absolute inset-0">
          <img
            src={image}
            alt={imageAlt || ""}
            className="w-full h-full object-cover"
            loading="eager"
          />
        </motion.div>
        {/*
          Two gradients rather than one: a horizontal wash keeps the headline
          legible on desktop, and a vertical one does the same on mobile where
          the text sits over the middle of the image.
        */}
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
        <div className="absolute inset-0 lg:hidden bg-gradient-to-b from-ink/90 via-ink/85 to-ink/80" />
      </div>

      <div className="container-emilio relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 lg:items-center py-10 lg:py-20">
          {/* Headline block — first on every screen size. */}
          <div className="lg:col-span-7 lg:col-start-1 lg:row-start-1">
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}

            <RevealText
              as="h1"
              text={headline}
              className="mt-5 font-display font-extrabold text-white text-hero"
            />

            {intro && (
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.6 }}
                className="mt-5 text-mutedtext text-base md:text-[17px] leading-relaxed max-w-xl"
              >
                {intro}
              </motion.p>
            )}
          </div>

          {/*
            The form sits directly under the headline on a phone. It was
            previously below the reassurance row and both buttons, which put it
            746px down an 844px screen — visible only as a sliver, and covered
            by the floating WhatsApp bar. A quote form nobody scrolls to is the
            exact failure the brief warns about.
          */}
          {showQuoteForm && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:row-span-2"
            >
              <HeroQuoteForm defaultService={defaultService} />
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.6 }}
            className="lg:col-span-7 lg:col-start-1 lg:row-start-2"
          >
            <HeroFeatures />

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="hero-quote-cta"
                className="inline-flex items-center gap-2 rounded-full bg-emerald text-ink px-6 py-3.5 font-semibold text-sm hover:bg-emerald-light transition-colors"
              >
                Get a Free Quote <ArrowUpRight className="w-4 h-4" />
              </Link>
              <WhatsAppButton label="WhatsApp Us" service={defaultService || undefined} />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="rule-green relative z-10" />
    </section>
  );
}
